import { supabase } from '../lib/supabase';
import { notificationService } from './notificationService';

export const eventReminderService = {
  /**
   * Create a new event reminder
   */
  async createReminder(reminderData) {
    try {
      const { data, error } = await supabase
        ?.from('event_reminders')
        ?.insert({
          event_id: reminderData?.eventId,
          reminder_name: reminderData?.reminderName,
          reminder_type: reminderData?.reminderType,
          recipient_type: reminderData?.recipientType,
          send_before_hours: reminderData?.sendBeforeHours,
          subject: reminderData?.subject,
          message: reminderData?.message,
          scheduled_at: reminderData?.scheduledAt,
          status: 'scheduled'
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating reminder:', error);
      throw error;
    }
  },

  /**
   * Get all reminders for an event
   */
  async getEventReminders(eventId) {
    try {
      const { data, error } = await supabase
        ?.from('event_reminders')
        ?.select('*')
        ?.eq('event_id', eventId)
        ?.order('scheduled_at', { ascending: true });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching event reminders:', error);
      throw error;
    }
  },

  /**
   * Get all reminders (admin only)
   */
  async getAllReminders() {
    try {
      const { data, error } = await supabase
        ?.from('event_reminders')
        ?.select(`
          *,
          event:events(
            id,
            title,
            scheduled_at,
            institute:institute_profiles(name)
          )
        `)
        ?.order('scheduled_at', { ascending: true });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching all reminders:', error);
      throw error;
    }
  },

  /**
   * Update reminder
   */
  async updateReminder(reminderId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('event_reminders')
        ?.update(updates)
        ?.eq('id', reminderId)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating reminder:', error);
      throw error;
    }
  },

  /**
   * Delete reminder
   */
  async deleteReminder(reminderId) {
    try {
      const { error } = await supabase
        ?.from('event_reminders')
        ?.delete()
        ?.eq('id', reminderId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error deleting reminder:', error);
      throw error;
    }
  },

  /**
   * Get recipients for a reminder based on recipient type
   */
  async getRecipients(recipientType, eventId) {
    try {
      let recipients = [];

      if (recipientType === 'students' || recipientType === 'both') {
        // Get all students who registered for events
        const { data: students, error: studentsError } = await supabase
          ?.from('event_registrations')
          ?.select(`
            user:user_profiles!event_registrations_user_id_fkey(
              user_id,
              name,
              email,
              phone
            )
          `)
          ?.eq('event_id', eventId)
          ?.eq('status', 'confirmed');

        if (studentsError) throw studentsError;

        recipients = recipients?.concat(
          students?.map(reg => ({
            id: reg?.user?.user_id,
            name: reg?.user?.name,
            email: reg?.user?.email,
            phone: reg?.user?.phone,
            type: 'student'
          })) || []
        );
      }

      if (recipientType === 'institutes' || recipientType === 'both') {
        // Get institute associated with the event
        const { data: event, error: eventError } = await supabase
          ?.from('events')
          ?.select(`
            institute:institute_profiles(
              user_id,
              name,
              email,
              phone
            )
          `)
          ?.eq('id', eventId)
          ?.single();

        if (eventError) throw eventError;

        if (event?.institute) {
          recipients?.push({
            id: event?.institute?.user_id,
            name: event?.institute?.name,
            email: event?.institute?.email,
            phone: event?.institute?.phone,
            type: 'institute'
          });
        }
      }

      return recipients;
    } catch (error) {
      console.error('Error getting recipients:', error);
      throw error;
    }
  },

  /**
   * Send reminder to all recipients
   */
  async sendReminder(reminderId) {
    try {
      // Get reminder details
      const { data: reminder, error: reminderError } = await supabase
        ?.from('event_reminders')
        ?.select('*')
        ?.eq('id', reminderId)
        ?.single();

      if (reminderError) throw reminderError;

      // Get recipients
      let recipients = await this.getRecipients(
        reminder?.recipient_type,
        reminder?.event_id
      );

      let sentCount = 0;
      let failedCount = 0;

      // Send to each recipient
      for (const recipient of recipients) {
        try {
          // Send email if reminder type includes email
          if (
            (reminder?.reminder_type === 'email' || reminder?.reminder_type === 'both') &&
            recipient?.email
          ) {
            const emailHtml = `
              <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                <h2 style="color: #2563eb;">${reminder?.subject || 'Event Reminder'}</h2>
                <div style="background-color: #f3f4f6; padding: 20px; border-radius: 8px; margin: 20px 0;">
                  <p style="white-space: pre-wrap;">${reminder?.message}</p>
                </div>
                <p style="color: #6b7280; font-size: 14px;">This is an automated reminder from StudyExpo Virtual Education Fair.</p>
              </div>
            `;

            await notificationService?.sendEmail({
              to: recipient?.email,
              subject: reminder?.subject || 'Event Reminder',
              html: emailHtml,
              type: 'event_reminder'
            });

            // Log email send
            await supabase?.from('event_reminder_logs')?.insert({
              reminder_id: reminderId,
              recipient_id: recipient?.id,
              recipient_email: recipient?.email,
              delivery_method: 'email',
              status: 'sent',
              sent_at: new Date()?.toISOString()
            });

            sentCount++;
          }

          // Send SMS if reminder type includes SMS
          if (
            (reminder?.reminder_type === 'sms' || reminder?.reminder_type === 'both') &&
            recipient?.phone
          ) {
            await notificationService?.sendSMS({
              to: recipient?.phone,
              message: reminder?.message?.substring(0, 160),
              type: 'event_reminder'
            });

            // Log SMS send
            await supabase?.from('event_reminder_logs')?.insert({
              reminder_id: reminderId,
              recipient_id: recipient?.id,
              recipient_phone: recipient?.phone,
              delivery_method: 'sms',
              status: 'sent',
              sent_at: new Date()?.toISOString()
            });

            sentCount++;
          }
        } catch (error) {
          console.error(`Error sending to recipient ${recipient?.id}:`, error);
          failedCount++;

          // Log failure
          await supabase?.from('event_reminder_logs')?.insert({
            reminder_id: reminderId,
            recipient_id: recipient?.id,
            recipient_email: recipient?.email,
            recipient_phone: recipient?.phone,
            delivery_method: reminder?.reminder_type === 'email' ? 'email' : 'sms',
            status: 'failed',
            error_message: error?.message
          });
        }
      }

      // Update reminder status
      await supabase
        ?.from('event_reminders')
        ?.update({
          status: failedCount === recipients?.length ? 'failed' : 'sent',
          sent_at: new Date()?.toISOString(),
          recipients_count: recipients?.length,
          sent_count: sentCount,
          failed_count: failedCount
        })
        ?.eq('id', reminderId);

      return {
        success: true,
        totalRecipients: recipients?.length,
        sentCount,
        failedCount
      };
    } catch (error) {
      console.error('Error sending reminder:', error);

      // Mark reminder as failed
      await supabase
        ?.from('event_reminders')
        ?.update({ status: 'failed' })
        ?.eq('id', reminderId);

      throw error;
    }
  },

  /**
   * Check for reminders that need to be sent
   */
  async checkPendingReminders() {
    try {
      const now = new Date();

      // Get reminders scheduled to be sent now or in the past
      const { data: reminders, error } = await supabase
        ?.from('event_reminders')
        ?.select('*')
        ?.eq('status', 'scheduled')
        ?.lte('scheduled_at', now?.toISOString());

      if (error) throw error;

      const results = [];

      for (const reminder of reminders || []) {
        try {
          const result = await this.sendReminder(reminder?.id);
          results?.push({ reminderId: reminder?.id, ...result });
        } catch (error) {
          console.error(`Failed to send reminder ${reminder?.id}:`, error);
          results?.push({
            reminderId: reminder?.id,
            success: false,
            error: error?.message
          });
        }
      }

      return results;
    } catch (error) {
      console.error('Error checking pending reminders:', error);
      throw error;
    }
  },

  /**
   * Get reminder logs for a specific reminder
   */
  async getReminderLogs(reminderId) {
    try {
      const { data, error } = await supabase
        ?.from('event_reminder_logs')
        ?.select('*')
        ?.eq('reminder_id', reminderId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching reminder logs:', error);
      throw error;
    }
  },

  /**
   * Get all events for reminder scheduling
   */
  async getEvents() {
    try {
      const { data, error } = await supabase
        ?.from('events')
        ?.select(`
          id,
          title,
          description,
          event_type,
          scheduled_at,
          duration_minutes,
          institute:institute_profiles(name)
        `)
        ?.gte('scheduled_at', new Date()?.toISOString())
        ?.order('scheduled_at', { ascending: true });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching events:', error);
      throw error;
    }
  }
};
